import React, {Component} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment/moment';
import {
  BillContainer,
  BillEtaLabel,
  BillEtaValue,
  BillFutureOrderHead
} from 'routes/App/ordersV2/OrdersList/styles';
import DisplayField from 'components/fields/DisplayField';
import {Body1, Caption1, Caption2} from 'components/typography';
import {BillDottedLine} from 'routes/App/ordersV2/OrderDetails/styles';
import {FlexContainer, FlexItem} from 'components/layout';
import SubTitle from 'components/SubTitle/styles';
import {
  ORDER,
  ORDER_TYPES,
  ORDER_TYPE,
  ORDER_SOURCE,
  DELIVERY_PROVIDER_TYPE
} from 'helpers/constants/orders_v2';
import {groupDigital} from 'helpers/functionUtils/groupByDigital';
import {phoneFormatForView} from 'helpers/functionUtils/helpFunctions';
import {ValueTitle} from 'routes/App/ordersV2/components/Bill/styles';
import {checkIsFutureOrder} from 'helpers/functionUtils/ordersUtils';
import UberEats from 'assets/uber-eats.svg';
import Doordash from 'assets/icon-doordash.svg';
import Grubhub from 'assets/icon-grubhub.svg';
import IconSauceDelivery from 'assets/icon-sauce-delivery.svg';

const mapOrderOptionsHelper = (
  item,
  {fromSecondLevel, quantity = 1} = {},
  useSalesPackage
) => (
  <>
    <Caption1 className='block '>{item.sectionName}</Caption1>
    <FlexContainer justifyContent='space-between'>
      <FlexItem className='wordWrap'>
        <Body1>
          {!fromSecondLevel && <b>{`${item.quantity} x `}</b>}
          {item.name}
        </Body1>
      </FlexItem>
      <FlexItem
        style={{
          width: '120px',
          paddingRight: '40px',
          paddingLeft: '10px',
          textAlign: 'right',
          alignSelf: 'self-end'
        }}
      >
        <Body1>
          {' '}
          {!useSalesPackage &&
            `$${groupDigital(
              Number(Number(item.baseUnitPrice) || 0).toFixed(2)
            )}`}
        </Body1>
      </FlexItem>
    </FlexContainer>
    {Array.isArray(item.modifiers) && item.modifiers.length > 0 && (
      <div className='marginLeft2l'>
        {item.modifiers.map(
          (option) =>
            option &&
            mapOrderOptionsHelper(
              option,
              {
                fromSecondLevel: true,
                quantity: item.quantity * quantity
              },
              useSalesPackage
            )
        )}
      </div>
    )}
    <div style={{paddingRight: '120px'}}>
      <Body1 className='wordWrap'>
        <i>{item.specialNote && `(${item.specialNote})`}</i>
      </Body1>
    </div>
  </>
);

const deepCount = (item) => {
  let total = Number(item.total || 0);

  if (Array.isArray(item.modifiers) && item.modifiers.length > 0) {
    item.modifiers.forEach((option) => {
      if (option) {
        total += (item.quantity || 0) * deepCount(option);
      }
    });
  }

  return total;
};

export default class Bill extends Component {
  render() {
    const {order, locationName, useSalesPackage, timezone} = this.props;

    return (
      <BillContainer>
        {checkIsFutureOrder(order) &&
          order[ORDER.ORDER_STATUS] !== 'Canceled' && (
            <BillFutureOrderHead className='marginBottomXs'>
              ***FUTURE ORDER***
            </BillFutureOrderHead>
          )}
        {order[ORDER.ORDER_STATUS] === 'Canceled' && (
          <BillFutureOrderHead className='marginBottomXs CanceledOrder'>
            ***CANCELED ORDER***
          </BillFutureOrderHead>
        )}
        <DisplayField
          label={<BillEtaLabel>ETA</BillEtaLabel>}
          value={
            checkIsFutureOrder(order) ? (
              <BillEtaValue>
                For{' '}
                {moment(`${order[ORDER.DATE]}Z`)
                  .utcOffset(0)
                  .format('MMMM D, YYYY')}
                ,{' '}
                <span>
                  {moment(`${order[ORDER.DATE]}Z`)
                    .utcOffset(0)
                    .format('h:mm A')}
                </span>
              </BillEtaValue>
            ) : (
              <BillEtaValue>
                <span>
                  Today,{' '}
                  {moment(`${order[ORDER.DATE]}Z`)
                    .utcOffset(0)
                    .format('h:mm A')}
                </span>
              </BillEtaValue>
            )
          }
          className='marginBottomXs widthAuto'
          spaceBetween
          flexItemFullWidth
        />

        {order[ORDER.AHEAD_TIME] && (
          <>
            <BillDottedLine className='marginBottomSm withoutOpacity' />
            <DisplayField
              label={<Caption1>Placed at</Caption1>}
              value={
                <Caption1>
                  {moment(`${order[ORDER.CREATED_AT]}Z`)
                    .utcOffset(Number(timezone) || 0)
                    .format('MMM D. h:mm A')}
                </Caption1>
              }
              className='marginBottomXs'
              spaceBetween
            />
            <BillDottedLine className='marginBottomSm withoutOpacity' />
          </>
        )}

        <DisplayField
          label={<Body1>Order Type:</Body1>}
          value={
            <div className='typeWithIcon'>
              {order[ORDER.SOURCE] === ORDER_SOURCE.SAUCE && (
                <img src={IconSauceDelivery} alt='logo' />
              )}
              {order[ORDER.SOURCE] === ORDER_SOURCE.SAUCE_DSP && (
                <img src={IconSauceDelivery} alt='logo' />
              )}
              {order[ORDER.SOURCE] === ORDER_SOURCE.UBER && (
                <img src={UberEats} alt='logo' />
              )}
              {order[ORDER.SOURCE] === ORDER_SOURCE.DOORDASH && (
                <img src={Doordash} alt='logo' />
              )}
              {order[ORDER.SOURCE] === ORDER_SOURCE.GRUBHUB && (
                <img src={Grubhub} alt='logo' />
              )}
              {(() => {
                if (
                  order[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
                  order[ORDER.DELIVERY]?.provider ===
                    DELIVERY_PROVIDER_TYPE.LOCATION
                ) {
                  return <Caption1>Self-delivery</Caption1>;
                }
                if (order[ORDER.ORDER_TYPE] === ORDER_TYPE.DINE_IN) {
                  return <Caption1>Dine in</Caption1>;
                }
                if (order[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY) {
                  return <Caption1>Delivery</Caption1>;
                }
                return <Caption1>Pick up</Caption1>;
              })()}
            </div>
          }
          className='marginBottomXs ordersV2BillType'
          spaceBetween
        />
        {ORDER_TYPES[order[ORDER.ORDER_TYPE]] ===
          ORDER_TYPES[ORDER_TYPE.DELIVERY] &&
          order[ORDER.DELIVERY]?.address?.formattedAddress && (
            <DisplayField
              label={<Body1>Delivery Address:</Body1>}
              value={
                <Caption1>
                  {order[ORDER.DELIVERY]?.address?.formattedAddress}
                </Caption1>
              }
              className='marginBottomXs'
              spaceBetween
            />
          )}

        <BillDottedLine className='marginBottomSm withoutOpacity' />

        <DisplayField
          label={<Body1>Order ID:</Body1>}
          value={
            <Caption1 className='wordWrap'>
              {order[ORDER.SOURCE] === ORDER_SOURCE.SAUCE
                ? order[ORDER.INVOICE_ID] || ''
                : order[ORDER.EXTERNAL_ID] || ''}
            </Caption1>
          }
          className='marginBottomXs'
          flexWrap
          flexItemFullWidth
          spaceBetween
        />

        <BillDottedLine className='marginBottomXl withoutOpacity' />

        <SubTitle className='marginBottomMd wordWrap'>
          {order[ORDER.CUSTOMER]?.fullName || ''}
        </SubTitle>
        {order[ORDER.CUSTOMER]?.phone && (
          <DisplayField
            label={<Body1>Phone Number:</Body1>}
            value={
              <Caption1>
                {phoneFormatForView(order[ORDER.CUSTOMER]?.phone)}
              </Caption1>
            }
            className='marginBottomXs'
            spaceBetween
          />
        )}
        {order[ORDER.CUSTOMER]?.email && (
          <DisplayField
            label={<Body1>Email Address:</Body1>}
            value={
              <Caption1 className='wordWrap'>
                {order[ORDER.CUSTOMER]?.email}
              </Caption1>
            }
            className='marginBottomXs'
            flexWrap
            flexItemFullWidth
            spaceBetween
          />
        )}

        <DisplayField
          label={<Body1>Location:</Body1>}
          value={<Caption1>{locationName}</Caption1>}
          className='marginBottomXl'
          spaceBetween
        />

        <BillDottedLine className='marginBottomXl' />

        {order[ORDER.CART]?.items?.map((item) => (
          <>
            {mapOrderOptionsHelper(item, {}, useSalesPackage)}
            <div style={{position: 'relative', height: '20px'}}>
              <Caption1
                style={{
                  display: 'block',
                  position: 'absolute',
                  top: '0px',
                  right: '0'
                }}
              >
                {!useSalesPackage &&
                  `$${groupDigital(Number(deepCount(item) || 0).toFixed(2))}`}
              </Caption1>
            </div>
          </>
        ))}

        <BillDottedLine className='marginTopXl marginBottomXl' />

        {order[ORDER.CART]?.specialNote && (
          <>
            <DisplayField
              label={<Body1>Note:</Body1>}
              value={<Caption1>{order[ORDER.CART]?.specialNote}</Caption1>}
              className='marginBottomXs'
              spaceBetween
            />
            <BillDottedLine className='marginBottomXl' />
          </>
        )}

        {!useSalesPackage && (
          <>
            <DisplayField
              label={<Caption1>Subtotal:</Caption1>}
              value={
                <Caption1>
                  ${' '}
                  {groupDigital(
                    Number(order[ORDER.TOTALS]?.locationSubtotal || 0).toFixed(
                      2
                    )
                  )}
                </Caption1>
              }
              spaceBetween
              className='marginBottomXs'
            />
            {!!order[ORDER.PRICE_ADJUSTMENTS]?.length &&
              order[ORDER.PRICE_ADJUSTMENTS].map((adjustment) => (
                <DisplayField
                  label={<Caption1>Adjustment:</Caption1>}
                  value={
                    <Caption1>
                      ${' '}
                      {groupDigital(
                        Number(adjustment.sourceAmount || 0).toFixed(2)
                      )}
                    </Caption1>
                  }
                  spaceBetween
                  className='marginBottomXs'
                />
              ))}
            <DisplayField
              label={<Caption1>Tax:</Caption1>}
              value={
                <Caption1>
                  ${' '}
                  {groupDigital(
                    Number(order[ORDER.TAXES]?.salesTaxAmount || 0).toFixed(2)
                  )}
                </Caption1>
              }
              spaceBetween
              className='marginBottomXs'
            />
            {!!Number(order[ORDER.TAXES]?.alcoholTaxAmount) && (
              <DisplayField
                label={<Caption1>Alcohol Tax:</Caption1>}
                value={
                  <Caption1>
                    ${' '}
                    {groupDigital(
                      Number(order[ORDER.TAXES]?.alcoholTaxAmount).toFixed(2)
                    )}
                  </Caption1>
                }
                spaceBetween
                className='marginBottomXs'
              />
            )}
            {!!Number(
              order[ORDER.FEES]?.locationOptionalServiceChargeAmount
            ) && (
              <DisplayField
                label={<Caption1>Service Charge:</Caption1>}
                value={
                  <Caption1>
                    ${' '}
                    {groupDigital(
                      Number(
                        order[ORDER.FEES]?.locationOptionalServiceChargeAmount
                      ).toFixed(2)
                    )}
                  </Caption1>
                }
                spaceBetween
                className='marginBottomXs'
              />
            )}

            {!order[ORDER.DELIVERY]?.isDispatch && (
              <>
                {Number(order[ORDER.FEES]?.deliveryFee) ? (
                  <DisplayField
                    label={<Caption1>Delivery:</Caption1>}
                    value={
                      <Caption1>
                        ${' '}
                        {groupDigital(
                          Number(order[ORDER.FEES]?.deliveryFee || 0).toFixed(2)
                        )}
                      </Caption1>
                    }
                    className='marginBottomXs'
                    spaceBetween
                  />
                ) : null}
                {Number(order[ORDER.TIPS_AMOUNT]) > 0 && (
                  <DisplayField
                    label={<Caption1>Tip:</Caption1>}
                    value={
                      <Caption1>
                        ${' '}
                        {groupDigital(
                          Number(order[ORDER.TIPS_AMOUNT] || 0).toFixed(2)
                        )}
                      </Caption1>
                    }
                    className='marginBottomXs'
                    spaceBetween
                  />
                )}
              </>
            )}
            {order[ORDER.DISCOUNTS]?.length > 0 &&
              order[ORDER.DISCOUNTS]?.map((discount) => (
                <DisplayField
                  label={
                    <ValueTitle className='wordWrap'>
                      Discount ({discount.Description}):
                    </ValueTitle>
                  }
                  value={
                    <Caption1>
                      - ${' '}
                      {groupDigital(
                        Number(
                          discount.MerchantDiscount || discount.Sum || 0
                        ).toFixed(2)
                      )}
                    </Caption1>
                  }
                  className='marginBottomXs'
                  spaceBetween
                />
              ))}
            <br />
            <DisplayField
              label={<SubTitle>Total:</SubTitle>}
              value={
                <Caption1>
                  ${' '}
                  {groupDigital(
                    Number(order[ORDER.TOTALS]?.locationTotal || 0).toFixed(2)
                  )}
                </Caption1>
              }
              spaceBetween
            />
            <BillDottedLine className='marginTopXl marginBottomSm' />
          </>
        )}
        <Caption1 className='textAlignCenter '>
          Thank you for your order. <br />
          Order directly with us and save up to 20-40% on every order.
        </Caption1>
      </BillContainer>
    );
  }
}

Bill.defaultProps = {
  useSalesPackage: false
};

Bill.propTypes = {
  order: PropTypes.object.isRequired,
  locationName: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
  useSalesPackage: PropTypes.bool
};
